import { Table, Typography, Flex, Space, Tag, Tooltip } from "antd"
import moment from "moment"
// import { useCollection } from "../../../../hooks/useCollection"
// import { OrderLocation } from "../../../Orders/OrderLocation"
import { generateFilter } from "../../../Orders/JobFilters"
// import RollAssignDrawer from "../RollAssign/RollAssignDrawer"
import { returnProperMachineTag } from "../../../utils/ReturnProperMachineTag"
import { ASAPOrder, OrderAndPrefix } from "../../../Orders/Helpers/OrderAndPrefix"
import FillStopRequest from "../Action/FillStopRequest"
import "../../../../styles/OrderTable.css"

import ShowOrderDetails from "../Action/ShowOrderDetails"
import MakePriorityJob from "../Action/MakePriorityJob"
import { useEffect, useState } from "react"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { calculateProjectedMeters } from "../../../Orders/Helpers/CalculateProjectedMeters"
import MarkReadyForPrint from "../Action/MarkReadyForPrint"
// import { FilterFilled } from "@ant-design/icons"

const { Text } = Typography

const getActionStatus = (record) => {
  if (record.requestedToStop) return "pending"
  if (record.readyForPrinting) return "ready"
  return "pending"
}

export default function ActiveTable() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  // const { data: rollData, loading: rollLoading } = useCollection("rolls")

  useEffect(() => {
    setLoading(true)
    const ref = collection(db, "orders")
    const q = query(ref, where("orderStatus", "==", 2))

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        ...doc.data(),
        key: doc.id,
        id: doc.id,
      }))

      // Use the shared function
      const resultsWithProjectedMeters = await Promise.all(
        results.map((order) => calculateProjectedMeters(order)),
      )

      resultsWithProjectedMeters.sort((a, b) => b.created?.seconds - a.created?.seconds)

      setData(resultsWithProjectedMeters)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  //filters
  let clientFilters = generateFilter(data, ["client", "client"])
  let productFilter = generateFilter(data, ["selectedProduct", "product", "productName"])
  let materialFilter = generateFilter(data, ["selectedProduct", "material"])
  let machineFilter = generateFilter(data, ["selectedProduct", "machine"])

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
      width: "10%",
      key: "orderNumber",
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "right",
      filters: clientFilters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
      sorter: (a, b) => {
        const clientA = a.client?.client || ""
        const clientB = b.client?.client || ""
        return clientA.localeCompare(clientB)
      },
      width: "12%",
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
      key: "client",
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      filters: productFilter,
      onFilter: (value, record) => record.selectedProduct.product.productName.startsWith(value),
      filterSearch: true,
      width: "15%",
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
      key: "product",
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "center",
      width: "10%",
      key: "manager",
      // We replace `ellipsis: true` with a more functional Tooltip
      render: (user) => {
        // Safety check if the user value is not a valid string
        if (!user || typeof user !== "string") {
          return "—" // Display a dash if data is missing
        }

        // Split the email at the "@" and take the first part
        const name = user.split("@")[0]

        // Display the extracted name, and show the full email on hover
        return (
          <Tooltip title={user}>
            <Text>{name}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: "Manuf.",
      dataIndex: ["manufacturer", "company"],
      align: "center",

      ellipsis: true,
      key: "manufacturer",
      render: (companyName) => {
        // Guard against cases where the company name might be missing
        if (!companyName || typeof companyName !== "string") {
          return "—" // Return a dash if no name is available
        }

        // The logic to create the abbreviation
        const initials = companyName
          .split(" ") // -> ["ART", "LABEL", "SIA"]
          .filter(Boolean) // Removes any empty strings from double spaces
          .map((word) => word[0]) // -> ["A", "L", "S"]
          .slice(0, 2) // -> ["A", "L"]
          .join("") // -> "AL"
          .toUpperCase() // -> "AL"

        // Display the abbreviation, but show the full name on hover
        return (
          <Tooltip title={companyName}>
            <Text>{initials}</Text>
          </Tooltip>
        )
      },
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      filters: machineFilter,
      onFilter: (value, record) => record.selectedProduct.machine.startsWith(value),
      filterSearch: true,
      render: (value) => {
        return returnProperMachineTag(value)
      },
      key: "machine",
    },

    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "center",
      filters: materialFilter,
      onFilter: (value, record) => record.selectedProduct.material.startsWith(value),
      filterSearch: true,
      key: "material",
      width: "10%",
    },
    {
      title: "Price (€)",
      dataIndex: "price",
      align: "center",
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      render: (value) => {
        if (!value) return "—"
        return <Text>{value.toFixed(2)}</Text>
      },
      width: "8%",
      key: "price",
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "center",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value, record) => {
        if (record.readRollWidth) {
          return <Text>{record.rollWidth}</Text>
        } else {
          return <Text>{value}</Text>
        }
      },
    },
    {
      title: "Proj. M.",
      dataIndex: "projectedMeters",
      align: "center",
      width: "10%",
      sorter: (a, b) => a.projectedMeters - b.projectedMeters,
      render: (value) => <Text>{value ? `${Math.round(value)}m` : "0m"}</Text>,
      key: "projectedMeters",
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      key: "created",
      render: (value, record) => {
        const dueDate = moment.unix(value.seconds)
        const isOverdue = dueDate.isBefore(moment(), "day")
        if (isOverdue) {
          return <Tag color="red"> {dueDate.format("DD.MM.YYYY")}</Tag>
        } else {
          return <Tag color="blue">{dueDate.format("DD.MM.YYYY")}</Tag>
        }
      },
    },
    // {
    //   title: "Created",
    //   dataIndex: "created",
    //   align: "center",
    //   key: "created",
    //   render: (value, record) => (
    //     <>
    //       <Text>{value && moment.unix(value.seconds).format("DD.MM.YYYY")}</Text>
    //     </>
    //   ),
    // },
    {
      title: "Action",
      align: "center",
      key: "action",
      width: "5%",
      filters: [
        { text: "Pending", value: "pending" },
        { text: "Ready", value: "ready" },
      ],
      onFilter: (value, record) => getActionStatus(record) === value,
      render: (value, record) => (
        <>
          <Flex gap="small">
            <ShowOrderDetails data={record} />
            {/* <RollAssignDrawer
              hasRoll={record.hasRoll}
              orderData={record}
              allRolls={rollData}
              rollLoading={rollLoading}
            /> */}
            <MakePriorityJob data={record} />
            {record.requestedToStop ? <FillStopRequest data={record} /> : ""}
            <MarkReadyForPrint record={record} />
          </Flex>
        </>
      ),
    },
  ]

  return (
    <>
      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={loading}
        columns={defaultColumns}
        dataSource={data}
        rowKey={(record) => record.id}
        pagination={{ defaultPageSize: "50", showSizeChanger: true }}
        rowClassName={(record) => {
          const classes = []
          if (record.priorityOrder) classes.push("red-background")
          if (record.readyForPrinting) classes.push("ready-row")
          return classes.join(" ")
        }}
        scroll={{
          x: "max-content",
        }}
      />
    </>
  )
}
